import { useEffect, useRef, useState } from 'react';
import { useProject } from '../../../contexts/ProjectContext';
import { dashboardService } from '../../../services/dashboard';

function formatTime(iso) {
  if (!iso) return '—';
  return new Date(iso).toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}

function eventModelLine(evt) {
  if (evt.decision === 'routed' && evt.output_model) {
    return `${evt.input_model} → ${evt.output_model}`;
  }
  return evt.input_model || 'unknown model';
}

function eventDecisionLabel(evt) {
  if (evt.decision === 'routed') return 'Routed';
  if (evt.decision === 'cache_hit') return 'Cached';
  if (evt.decision === 'local' || evt.decision === 'fallback') return 'Passthrough';
  if (evt.decision === 'error') return 'Error';
  return evt.decision || '—';
}

export default function TestStep({ onHasEventsChange }) {
  const { selectedHash } = useProject();
  const sinceRef = useRef(new Date().toISOString());
  const [events, setEvents] = useState([]);

  useEffect(() => {
    if (!selectedHash) return undefined;

    const poll = () => {
      dashboardService.recentEvents(
        selectedHash,
        { since: sinceRef.current, page_size: 20 },
        (res) => {
          const results = res.data?.results || [];
          setEvents(results);
          onHasEventsChange?.(results.length > 0);
        },
        console.error,
      );
    };

    poll();
    const id = setInterval(poll, 5000);
    return () => clearInterval(id);
  }, [selectedHash, onHasEventsChange]);

  const hasEvents = events.length > 0;

  return (
    <div>
      <h1 className="text-2xl font-semibold mb-2">Send a test request</h1>
      <p className="text-sm opacity-60 mb-8">
        Run the snippet from the previous step. We will detect requests as they arrive.
      </p>

      <div className="rounded-xl border border-theme-secondary bg-theme-main overflow-hidden">
        <div className="flex items-center gap-3 px-4 py-3 border-b border-theme-secondary/60">
          {!hasEvents && (
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-theme-primary opacity-60" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-theme-primary" />
            </span>
          )}
          <span className="text-sm font-medium">
            {hasEvents ? `${events.length} request${events.length === 1 ? '' : 's'} detected` : 'Waiting for your first request…'}
          </span>
        </div>

        {hasEvents ? (
          <div className="divide-y divide-theme-secondary/50">
            {events.map((evt) => (
              <div key={evt.hash || evt.id} className="px-4 py-3 flex items-center justify-between gap-4 text-sm">
                <div className="min-w-0">
                  <div className="font-mono text-xs truncate">{eventModelLine(evt)}</div>
                  <div className={`text-xs mt-0.5 ${evt.decision === 'routed' ? 'text-emerald-400' : 'opacity-50'}`}>
                    {eventDecisionLabel(evt)}
                  </div>
                </div>
                <div className="text-xs opacity-50 shrink-0">{formatTime(evt.create_date)}</div>
              </div>
            ))}
          </div>
        ) : (
          <div className="px-4 py-12 text-center text-sm opacity-50">
            No requests yet. Send one using your integration snippet.
          </div>
        )}
      </div>
    </div>
  );
}
